import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ITextSnippetDialogData } from '@modeso/aumico-lib-annualreport-fe';
import {
    applyColgroupWidths,
    convertFileToBase64,
    Indent,
    LineHeight,
    OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan,
    validateNoEmptyField,
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import type { ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Subject } from 'rxjs';
import { localizationKeys } from '../../constants/localizationKeys';

const CREATE_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_create_dialog_title;

@UntilDestroy()
@Component({
    selector: 'lib-text-snippet-component',
    templateUrl: 'text-snippet.component.html',
    styleUrls: ['text-snippet.component.scss'],
})
export class TextSnippetComponent implements OnInit, AfterViewInit, OnDestroy {
    public formGroup: UntypedFormGroup | undefined;
    public titleLocalizationKey: string = CREATE_TITLE_LOCALIZATION_KEY;
    public editMode = false;
    private textSnippet: ITextSnippetDTO | undefined;
    public saveButtonPressed = new Subject<ITextSnippetDTO>();
    public tiptapEditor: Editor | null = null;
    public tiptapValue = '';

    @ViewChild('tiptapHost') tiptapHost?: ElementRef<HTMLDivElement>;

    constructor(
        private readonly matIconRegistry: MatIconRegistry,
        private readonly dialogRef: MatDialogRef<TextSnippetComponent, void>,
        private readonly domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) private readonly data: ITextSnippetDialogData | undefined,
    ) {
        this.addSvgIcon('interface-delete 1');
        this.initializeForm();
    }

    ngOnInit(): void {
        if (this.data != null && this.data.titleLocalizationKey == null) {
            throw new Error('Title localization key in text snippet dialog data is undefined');
        }

        if (this.data != null && this.data.textSnippetData != null) {
            this.titleLocalizationKey = this.data.titleLocalizationKey;
            this.editMode = true;
            this.handleEditTextSnippet();
        }

        if (this.data != null && this.data.descriptionExistsError != null) {
            this.data.descriptionExistsError?.subscribe(() => {
                this.formGroup?.get('description')?.setErrors({ descriptionExists: true });
            });
        }
    }

    ngAfterViewInit(): void {
        this.ensureTiptapEditor();
        this.syncTiptapFromForm();
    }

    ngOnDestroy(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    private handleEditTextSnippet(): void {
        this.textSnippet = this.data.textSnippetData;
        this.formGroup?.controls['description'].setValue(this.data.textSnippetData.description);
        this.formGroup?.controls['title'].setValue(this.data.textSnippetData.title);
        this.formGroup?.controls['content'].setValue(this.data.textSnippetData.content);
        this.syncTiptapFromForm();
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private initializeForm(): void {
        this.formGroup = new UntypedFormGroup({
            description: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
            title: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
            content: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
        });
    }

    private ensureTiptapEditor(): void {
        if (this.tiptapEditor || !this.tiptapHost?.nativeElement) {
            return;
        }

        const extensions: AnyExtension[] = [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];

        this.tiptapEditor = new Editor({
            element: this.tiptapHost.nativeElement,
            extensions,
            content: this.tiptapValue,
            onUpdate: ({ editor }) => this.handleTiptapUpdate(editor.getHTML()),
        });
    }

    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) {
                return;
            }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) {
                    button.classList.add('is-selected');
                }
                button.textContent = item;
                button.addEventListener('click', () => {
                    currentCommand?.({ id: item, label: item });
                });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) {
                return;
            }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) {
                        return false;
                    }
                    if (currentItems.length === 0) {
                        return false;
                    }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') {
                        return true;
                    }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }

    private syncTiptapFromForm(): void {
        const nextValue = (this.formGroup?.get('content')?.value as string | null) ?? '';
        this.tiptapValue = applyColgroupWidths(nextValue);
        if (this.tiptapEditor) {
            this.tiptapEditor.commands.setContent(this.tiptapValue, false);
        }
    }

    private handleTiptapUpdate(html: string): void {
        this.tiptapValue = html;
        const control = this.formGroup?.get('content');
        if (control && control.value !== html) {
            control.setValue(html);
        }
    }

    async onTiptapImageSelected(file: File): Promise<void> {
        if (!file?.type?.startsWith('image/')) {
            return;
        }

        try {
            const base64 = await convertFileToBase64(file);
            if (!base64) {
                return;
            }
            this.tiptapEditor?.commands?.focus?.();
            (this.tiptapEditor as any)?.commands?.setImage?.({ src: base64 });
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    }

    public closeDialog(): void {
        this.dialogRef.close();
    }

    public onSaveTextSnippetClicked(): void {
        const description = this.formGroup?.get('description')?.value as unknown;
        if (description == null || typeof description !== 'string') {
            throw new Error('Description is undefined or not a string');
        }
        const title = this.formGroup?.get('title')?.value as unknown;
        if (title == null || typeof title !== 'string') {
            throw new Error('Title is undefined or not a string');
        }
        const content = this.formGroup?.get('content')?.value as unknown;
        if (content == null || typeof content !== 'string') {
            throw new Error('Content is undefined or not a string');
        }

        if (this.editMode) {
            this.textSnippet.title = title.trim();
            this.textSnippet.content = content.trim();
        } else {
            this.textSnippet = {
                description: description.trim(),
                title: title.trim(),
                content: content.trim(),
            };
        }

        this.saveButtonPressed.next(this.textSnippet);
    }
}
